import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import type {
  BootstrapQuestion,
  BootstrapQuestionRollup,
} from "@/interfaces/v2Analytics";
import type { Question, QuestionOption, Rollup } from "@/interfaces/questions";

/**
 * Structural match for the legacy questions entity-adapter state
 * (`EntityState<Question, string>` keyed by questionKey). All legacy
 * consumers (picker, DataCardLists, exports, saveView, selectionListener)
 * keep reading this exact shape.
 */
export type QuestionsAdapter = {
  ids: string[];
  entities: Record<string, Question>;
};

const EMPTY_ADAPTER: QuestionsAdapter = { ids: [], entities: {} };

/**
 * Deterministic fallback palette (same hex values as `colorCodes()` in
 * Rollups.tsx) — bootstrap carries no colors yet, so options/bands get a
 * stable color by position until the backend adds `color` to the embed.
 */
export const QUESTION_FALLBACK_COLORS = [
  "#FF5733",
  "#33FFCE",
  "#A45EEB",
  "#FAD7A0",
  "#8E44AD",
  "#2ECC71",
  "#9B59B6",
  "#E74C3C",
  "#3498DB",
  "#F39C12",
];

/** Available when `markets` is absent/empty, else every selected market must be covered. */
function isAvailableInMarkets(
  markets: number[] | undefined,
  marketIds: number[],
): boolean {
  if (!markets || markets.length === 0) return true;
  return marketIds.every((m) => markets.includes(m));
}

function bandType(source: BootstrapQuestionRollup["source"]): string {
  return source === "USER" ? "USER_ROLLUP" : "SYSTEM_ROLLUP";
}

/**
 * Pure transformer: bootstrap catalog → legacy Question shape.
 *  - market-filters questions and options (absence ⇒ available);
 *  - flattens rollup bands into one selectable legacy `Rollup` per band,
 *    with `type` derived from the scheme `source` (reference-mode picks);
 *  - dedups on `code` (matrix cells share `id` — never dedup on id);
 *  - sorts matrix cells by rowIndex within their fan-out family;
 *  - applies session color overrides and the user-rollups override.
 */
export function transformBootstrapQuestions(
  catalog: BootstrapQuestion[] | null,
  marketIds: number[],
  colorOverrides: Record<string, string>,
  userRollupsOverride: Record<string, Rollup[]>,
): QuestionsAdapter {
  if (!catalog || catalog.length === 0) return EMPTY_ADAPTER;

  // Dedup on code (keep first) + market filter.
  const seen = new Set<string>();
  const kept: { q: BootstrapQuestion; idx: number }[] = [];
  catalog.forEach((q, idx) => {
    if (seen.has(q.code)) return;
    if (!isAvailableInMarkets(q.markets, marketIds)) return;
    seen.add(q.code);
    kept.push({ q, idx });
  });

  // Stable order: catalog order between families; rowIndex within a
  // matrix family (family = shared registry question `id`).
  const familyFirstIdx = new Map<number, number>();
  kept.forEach(({ q, idx }) => {
    if (!familyFirstIdx.has(q.id)) familyFirstIdx.set(q.id, idx);
  });
  kept.sort((a, b) => {
    const fa = familyFirstIdx.get(a.q.id)!;
    const fb = familyFirstIdx.get(b.q.id)!;
    if (fa !== fb) return fa - fb;
    return (a.q.rowIndex ?? a.idx) - (b.q.rowIndex ?? b.idx);
  });

  const ids: string[] = [];
  const entities: Record<string, Question> = {};

  kept.forEach(({ q }, position) => {
    // Compute a single optionColorById map from the UNFILTERED q.options list
    // (by index). This ensures every option has one stable color everywhere —
    // both in the primary options array and nested in band options.
    const optionColorById = new Map(
      q.options.map((o, i) => [
        o.id,
        colorOverrides[`option-${o.id}`] ??
          o.color ??
          QUESTION_FALLBACK_COLORS[i % QUESTION_FALLBACK_COLORS.length],
      ]),
    );

    const options: QuestionOption[] = q.options
      .filter((o) => isAvailableInMarkets(o.markets ?? q.markets, marketIds))
      .map((o) => ({
        optionId: o.id,
        optionKey: o.key,
        optionValue: Number(o.value) || 0,
        optionLabel: o.label,
        color: optionColorById.get(o.id)!,
        sequence: o.ordinal,
        type: "RESPONSE",
        markets: (o.markets ?? q.markets ?? []).map((id) => ({
          id,
          key: String(id),
          label: "",
        })),
      }));

    // Resolve a band's sourceValues back to option refs for display
    // (picker secondaryOptions + Manage Roll-ups modal).
    const optionByValue = new Map(q.options.map((o) => [o.value, o]));
    let bandCounter = 0;
    const catalogRollups: Rollup[] = q.rollups.flatMap((scheme) =>
      scheme.bands.map((band) => ({
        accessType: scheme.source,
        rollupColor:
          colorOverrides[`band-${band.id}`] ??
          band.color ??
          QUESTION_FALLBACK_COLORS[
            (q.options.length + bandCounter++) % QUESTION_FALLBACK_COLORS.length
          ],
        rollupId: band.id,
        rollupKey: band.key,
        rollupLabel: band.label,
        type: bandType(scheme.source),
        options: band.sourceValues
          .map((v) => optionByValue.get(v))
          .filter((o): o is NonNullable<typeof o> => o != null)
          .map((o) => ({
            optionId: o.id,
            optionKey: o.key,
            optionLabel: o.label,
            optionValue: Number(o.value) || 0,
            color: optionColorById.get(o.id)!,
            sequence: o.ordinal,
            type: "RESPONSE",
          })),
      })),
    );

    // The save response is authoritative for a question's USER rollups
    // within the session: keep non-USER schemes, replace USER ones.
    const override = userRollupsOverride[q.code];
    const rollups =
      override != null
        ? [
            ...catalogRollups.filter((r) => r.type !== "USER_ROLLUP"),
            ...override.map((r) => ({
              ...r,
              rollupColor: colorOverrides[`band-${r.rollupId}`] ?? r.rollupColor,
            })),
          ]
        : catalogRollups;

    const baseLabel = q.displayLabel ?? q.label;
    const displayLabel =
      q.kind === "MATRIX_CELL" && q.groupLabel
        ? `${q.groupLabel} — ${baseLabel}`
        : baseLabel;

    const question: Question = {
      questionDisplayLabel: displayLabel,
      questionGroup: q.groupLabel ?? q.category ?? q.code,
      questionKey: q.code,
      questionLabel: q.label,
      questionSequence: position,
      rollups,
      options,
    };

    ids.push(q.code);
    entities[q.code] = question;
  });

  return { ids, entities };
}

/**
 * Memoized store-facing selector. Recomputes only when the catalog, the
 * overlays, or the selected market change.
 */
export const selectQuestionsAdapter = createSelector(
  [
    (state: RootState) => state.activeGroup.questionsCatalog.catalog,
    (state: RootState) => state.activeGroup.questionsCatalog.colorOverrides,
    (state: RootState) =>
      state.activeGroup.questionsCatalog.userRollupsOverride,
    (state: RootState) => state.userSelection.markets,
  ],
  (catalog, colorOverrides, userRollupsOverride, markets) =>
    transformBootstrapQuestions(
      catalog,
      markets ?? [],
      colorOverrides,
      userRollupsOverride,
    ),
);
